/**
 * Configuration Resolution Module
 *
 * Loads configuration from window.__APP_CONFIG__ which is set by app-config.js.
 *
 * Sources of app-config.js:
 * - Local development: Compiled from appconfig/config.*.ts via webpack middleware
 * - Deployed environments: Injected via ConfigMap from Vault
 *
 * @see appconfig/types.ts for configuration shape
 */
/** Singleton config instance */
let _config = null;
const DEFAULT_ENRICH_API_PREFIX = 'https://api.enrich-lo.groupbycloud.com';
const DEFAULT_ENRICH_AI_API_PREFIX = 'https://api-ai.enrich-lo.groupbycloud.com';
/**
 * Validates that the configuration object has the correct shape and required fields.
 *
 * @throws Error if configuration is invalid or missing required fields
 */
function validateConfig(config) {
    if (typeof config !== 'object' || config === null) {
        throw new Error('Configuration must be an object. Check your app-config.js file.');
    }
    const cfg = config;
    const errors = [];
    // Required string fields - use Extract to filter out symbol keys from index signature
    const requiredStrings = [
        'ccApiUrl',
        'customer',
        'ingestApiUrl',
        'searchApiUrl',
        'categoryApiUrl',
        'recsApiUrl',
        'seoManagerApiUrl',
        'enrichApiPrefix',
        'enrichAIApiPrefix',
    ];
    for (const field of requiredStrings) {
        if (typeof cfg[field] !== 'string' || cfg[field] === '') {
            errors.push(`'${field}' must be a non-empty string`);
        }
    }
    // Environment must be 'lower' | 'upper'
    if (cfg.environment !== 'lower' && cfg.environment !== 'upper') {
        const envValue = typeof cfg.environment === 'string' ? cfg.environment : typeof cfg.environment;
        errors.push(`'environment' must be 'lower' or 'upper', got '${envValue}'`);
    }
    // Required boolean fields
    const requiredBooleans = [
        'isNonPublicEnv',
        'skipAuthentication',
        'e2eCookieAuthentication',
    ];
    for (const field of requiredBooleans) {
        if (typeof cfg[field] !== 'boolean') {
            errors.push(`'${field}' must be a boolean`);
        }
    }
    if (errors.length > 0) {
        throw new Error(`Invalid configuration:\n  - ${errors.join('\n  - ')}\n`
            + 'Check your app-config.js file.');
    }
}
/**
 * Load configuration from window.__APP_CONFIG__
 *
 * @throws Error if window.__APP_CONFIG__ is not defined or invalid
 */
function getAppConfigFromWindow() {
    var _a, _b;
    if (typeof window === 'undefined' || !window.__APP_CONFIG__) {
        throw new Error('window.__APP_CONFIG__ is not defined.');
    }
    const configWithDefaults = Object.assign(Object.assign({}, window.__APP_CONFIG__), { enrichApiPrefix: (_a = window.__APP_CONFIG__.enrichApiPrefix) !== null && _a !== void 0 ? _a : DEFAULT_ENRICH_API_PREFIX, enrichAIApiPrefix: (_b = window.__APP_CONFIG__.enrichAIApiPrefix) !== null && _b !== void 0 ? _b : DEFAULT_ENRICH_AI_API_PREFIX });
    validateConfig(configWithDefaults);
    window.__APP_CONFIG__ = configWithDefaults;
    return configWithDefaults;
}
/**
 * Get the application configuration.
 *
 * Uses singleton pattern - configuration is loaded once and cached.
 *
 * @returns The application configuration
 * @throws Error if window.__APP_CONFIG__ is not defined or missing required fields
 *
 * @example
 * ```typescript
 * import { getConfig } from '@/config';
 *
 * const config = getConfig();
 * console.log(config.ccApiUrl);  // 'https://ccapi.gbiqa-lo.groupbycloud.com/ccapi'
 * console.log(config.customer);   // 'gbiqa'
 * ```
 */
export function getConfig() {
    if (!_config) {
        _config = getAppConfigFromWindow();
    }
    return _config;
}
